
import { Injectable } from '@angular/core';
import { AngularFirestore } from 'angularfire2/firestore';

// services
import { DBContextFirebaseService } from '../db-context/db-context.firebase.service';
import { StandardMetric, BloodPressureMetric, PlasmaGlucoseMetricTarget, PlasmaGlucoseMetric, BloodPressureTarget, PlasmaGlucoseTestingPlan, DiabetesComplication, Morbidity, Prescription, Achieve } from 'app/interfaces/metrics.interface';
import { Medication } from 'app/interfaces/admin/client-setup.interface';

@Injectable()
export class MetricsService {

  constructor(
    private afDB: AngularFirestore,
    private dBContextFirebaseService: DBContextFirebaseService,
  ) { }

  async getStandardMetric(metric: string, memberKey: string): Promise<StandardMetric[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection(metric)

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getStandardMetricTarget(metric: string, memberKey: string): Promise<string> {

    const docReference = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('targets')
      .doc(metric)

    const targetObj = await this.dBContextFirebaseService.get(docReference)
    if (!targetObj || !targetObj.target || targetObj.target === '') {
      return 'None';
    }
    return targetObj.target;
  }

  async getBloodPressuredMetric(memberKey: string): Promise<BloodPressureMetric[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('Bloodpressure')

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getBloodPressureMetricTarget(memberKey: string): Promise<BloodPressureTarget> {

    const docReference = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('targets')
      .doc('Bloodpressure')

    const bloodTarget: BloodPressureTarget = {
      targetDiastolic: 0,
      targetSystolic: 0
    };

    const targetObj: BloodPressureTarget = await this.dBContextFirebaseService.get(docReference)
    if (!targetObj || !targetObj.targetDiastolic) {
      return bloodTarget;
    }

    return targetObj;
  }

  async getPlasmaGlucoseMetricTarget(memberKey: string): Promise<PlasmaGlucoseMetricTarget> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('targets')
      .doc('GlucoseTarget')

    return await this.dBContextFirebaseService.get(querySnapshot)
  }

  async getPlasmaGlucoseTestingPlanTarget(memberKey: string): Promise<PlasmaGlucoseTestingPlan> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('targets')
      .doc('GlucoseSchedule')

    return await this.dBContextFirebaseService.get(querySnapshot)
  }

  async getPlasmaGlucoseMetric(memberKey: string): Promise<PlasmaGlucoseMetric[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('glucoseReadings')
      .orderBy('date', 'desc')

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getDiabetesComplications(memberKey: string): Promise<DiabetesComplication[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('diabetesComplications')
      .orderBy('dateOfDiagnosis', 'desc')

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getMorbidities(memberKey: string): Promise<Morbidity[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('morbidities')
      .orderBy('dateOfDiagnosis', 'desc')

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getPrescription(memberKey: string): Promise<Prescription[]> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('medication')
      .orderBy('endDate', 'desc')

    return await this.dBContextFirebaseService.getCollection(querySnapshot)
  }

  async getAchievePercentages(memberKey: string, achieveMetric: string): Promise<Achieve> {

    const querySnapshot = this.afDB.firestore
      .collection('users')
      .doc(memberKey)
      .collection('achieve')
      .doc(achieveMetric)

    return await this.dBContextFirebaseService.get(querySnapshot)
  }

}
